import { migrateProject, type Migration, MIGRATIONS } from '@/domain/model/migrations';
import type { ProjectDocument } from '@/domain/model/types';
import { parseProjectDocument } from '@/domain/model/validate';
import { getDatabase, type JnpDatabase, type ProjectRow, type SnapshotRow } from './db';
import {
  classifyStorageError,
  ProjectLoadError,
  StaleRevisionError,
  StorageFailureError,
} from './errors';

/**
 * Project persistence (docs/08 "Suggested public contracts", docs/09). Saves are
 * atomic, validated, and revision-checked: a stale tab can never silently overwrite a
 * newer save. Loads always validate and, when needed, migrate — after preserving the
 * original as a recovery snapshot.
 */
export interface ProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly revision: number;
  readonly nailCount: number;
  readonly designCount: number;
  readonly deletedAt: string | null;
}

export interface LoadedProject {
  readonly doc: ProjectDocument;
  readonly migrated: boolean;
  readonly notes: readonly string[];
  /** Set when the project is in the recoverable trash. */
  readonly deletedAt: string | null;
}

export interface SnapshotSummary {
  readonly seq: number;
  readonly projectId: string;
  readonly createdAt: string;
  readonly reason: SnapshotRow['reason'];
  readonly revision: number;
  readonly bytes: number;
}

export interface ProjectRepository {
  load(id: string): Promise<LoadedProject | null>;
  /** `expectedRevision` is the stored revision this tab last saw (null for a new project). */
  save(doc: ProjectDocument, expectedRevision: number | null): Promise<void>;
  list(options?: { includeDeleted?: boolean }): Promise<ProjectSummary[]>;
  /** Moves to the recoverable trash. */
  trash(id: string): Promise<void>;
  restore(id: string): Promise<void>;
  /** Permanently deletes the project, its snapshots, and thumbnail. */
  purge(id: string): Promise<void>;
  saveThumbnail(id: string, revision: number, svg: string): Promise<void>;
  getThumbnails(ids: readonly string[]): Promise<Map<string, string>>;
  addSnapshot(doc: ProjectDocument, reason: SnapshotRow['reason']): Promise<void>;
  listSnapshots(projectId: string): Promise<SnapshotSummary[]>;
  loadSnapshot(seq: number): Promise<ProjectDocument>;
}

/** Bounded recovery snapshots per project (docs/09 "Recovery and storage failure"). */
export const SNAPSHOT_POLICY = {
  maxPerProject: 12,
  maxBytesPerProject: 8 * 1024 * 1024,
  periodicIntervalMs: 5 * 60 * 1000,
} as const;

const utf8Length = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;

function summaryOf(row: ProjectRow): ProjectSummary {
  return {
    id: row.id,
    name: row.name,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    revision: row.revision,
    nailCount: row.nailCount,
    designCount: row.designCount,
    deletedAt: row.deletedAt,
  };
}

export class DexieProjectRepository implements ProjectRepository {
  private readonly dbProvider: () => JnpDatabase;
  private readonly migrations: readonly Migration[];

  constructor(
    dbProvider: () => JnpDatabase = getDatabase,
    migrations: readonly Migration[] = MIGRATIONS,
  ) {
    this.dbProvider = dbProvider;
    this.migrations = migrations;
  }

  private get db(): JnpDatabase {
    return this.dbProvider();
  }

  async load(id: string): Promise<LoadedProject | null> {
    let raw: unknown;
    let deletedAt: string | null;
    try {
      const db = this.db;
      const [row, meta] = await db.transaction('r', db.documents, db.projects, () =>
        Promise.all([db.documents.get(id), db.projects.get(id)]),
      );
      if (!row) return null;
      raw = row.document;
      deletedAt = meta?.deletedAt ?? null;
    } catch (error) {
      throw classifyStorageError(error);
    }
    const outcome = migrateProject(raw, this.migrations);
    if (!outcome.ok) throw new ProjectLoadError(outcome.reason, outcome.message);
    if (outcome.migrated) {
      // Preserve the original before any upgraded version can be saved over it.
      await this.writeSnapshot(
        id,
        (raw as { revision?: number }).revision ?? 0,
        raw,
        'before-migration',
      );
    }
    return { doc: outcome.doc, migrated: outcome.migrated, notes: outcome.notes, deletedAt };
  }

  async save(doc: ProjectDocument, expectedRevision: number | null): Promise<void> {
    const checked = parseProjectDocument(doc);
    if (!checked.ok) {
      throw new StorageFailureError(
        'invalid-data',
        `The project failed validation and was not saved: ${checked.issues[0]?.message ?? 'unknown issue'}`,
      );
    }
    const db = this.db;
    try {
      await db.transaction('rw', db.projects, db.documents, async () => {
        const existing = await db.projects.get(doc.id);
        const stored = existing?.revision ?? null;
        if (stored !== expectedRevision)
          throw new StaleRevisionError(doc.id, expectedRevision, stored);
        if (stored !== null && doc.revision <= stored)
          throw new StaleRevisionError(doc.id, expectedRevision, stored);
        const row: ProjectRow = {
          id: doc.id,
          name: doc.name,
          createdAt: doc.createdAt,
          updatedAt: doc.updatedAt,
          revision: doc.revision,
          schemaVersion: doc.schemaVersion,
          nailCount: doc.nails.length,
          designCount: doc.designs.length,
          deletedAt: existing?.deletedAt ?? null,
        };
        await db.projects.put(row);
        await db.documents.put({ id: doc.id, document: doc });
      });
    } catch (error) {
      if (error instanceof StaleRevisionError) throw error;
      const inner = (error as { inner?: unknown }).inner;
      if (inner instanceof StaleRevisionError) throw inner;
      throw classifyStorageError(error);
    }
  }

  async list(options: { includeDeleted?: boolean } = {}): Promise<ProjectSummary[]> {
    try {
      const rows = await this.db.projects.orderBy('updatedAt').reverse().toArray();
      return rows.filter((r) => options.includeDeleted || r.deletedAt === null).map(summaryOf);
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async trash(id: string): Promise<void> {
    await this.setDeleted(id, new Date().toISOString());
  }

  async restore(id: string): Promise<void> {
    await this.setDeleted(id, null);
  }

  private async setDeleted(id: string, deletedAt: string | null): Promise<void> {
    try {
      const updated = await this.db.projects.update(id, { deletedAt });
      if (updated === 0)
        throw new StorageFailureError(
          'invalid-data',
          'That project no longer exists on this device.',
        );
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async purge(id: string): Promise<void> {
    const db = this.db;
    try {
      await db.transaction(
        'rw',
        [db.projects, db.documents, db.thumbnails, db.snapshots],
        async () => {
          await db.projects.delete(id);
          await db.documents.delete(id);
          await db.thumbnails.delete(id);
          await db.snapshots.where('projectId').equals(id).delete();
        },
      );
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async saveThumbnail(id: string, revision: number, svg: string): Promise<void> {
    const db = this.db;
    try {
      await db.transaction('rw', db.thumbnails, async () => {
        // A late write for an older revision never replaces a newer preview.
        const existing = await db.thumbnails.get(id);
        if (existing && existing.revision > revision) return;
        await db.thumbnails.put({ projectId: id, revision, svg });
      });
    } catch {
      // Thumbnails are disposable previews; failing to store one never blocks saving.
    }
  }

  async getThumbnails(ids: readonly string[]): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    try {
      const rows = await this.db.thumbnails.bulkGet([...ids]);
      rows.forEach((row) => {
        if (row) map.set(row.projectId, row.svg);
      });
    } catch {
      // Missing thumbnails simply fall back to a placeholder.
    }
    return map;
  }

  async addSnapshot(doc: ProjectDocument, reason: SnapshotRow['reason']): Promise<void> {
    await this.writeSnapshot(doc.id, doc.revision, doc, reason);
  }

  private async writeSnapshot(
    projectId: string,
    revision: number,
    document: unknown,
    reason: SnapshotRow['reason'],
  ): Promise<void> {
    const db = this.db;
    try {
      await db.transaction('rw', db.snapshots, async () => {
        await db.snapshots.add({
          projectId,
          createdAt: new Date().toISOString(),
          reason,
          revision,
          bytes: utf8Length(document),
          document,
        });
        const rows = await db.snapshots.where('projectId').equals(projectId).sortBy('seq');
        // Newest first; drop the oldest beyond count or byte limits.
        rows.reverse();
        let bytes = 0;
        const remove: number[] = [];
        rows.forEach((row, i) => {
          bytes += row.bytes;
          if (
            i >= SNAPSHOT_POLICY.maxPerProject ||
            (i > 0 && bytes > SNAPSHOT_POLICY.maxBytesPerProject)
          ) {
            if (row.seq !== undefined) remove.push(row.seq);
          }
        });
        if (remove.length > 0) await db.snapshots.bulkDelete(remove);
      });
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async listSnapshots(projectId: string): Promise<SnapshotSummary[]> {
    try {
      const rows = await this.db.snapshots.where('projectId').equals(projectId).sortBy('seq');
      return rows.reverse().map((r) => ({
        seq: r.seq!,
        projectId: r.projectId,
        createdAt: r.createdAt,
        reason: r.reason,
        revision: r.revision,
        bytes: r.bytes,
      }));
    } catch (error) {
      throw classifyStorageError(error);
    }
  }

  async loadSnapshot(seq: number): Promise<ProjectDocument> {
    let row: SnapshotRow | undefined;
    try {
      row = await this.db.snapshots.get(seq);
    } catch (error) {
      throw classifyStorageError(error);
    }
    if (!row)
      throw new StorageFailureError('invalid-data', 'That recovery snapshot no longer exists.');
    const outcome = migrateProject(row.document, this.migrations);
    if (!outcome.ok) throw new ProjectLoadError(outcome.reason, outcome.message);
    return outcome.doc;
  }
}

let repository: ProjectRepository | null = null;

export function getProjectRepository(): ProjectRepository {
  repository ??= new DexieProjectRepository();
  return repository;
}

export function setProjectRepositoryForTesting(repo: ProjectRepository | null): void {
  repository = repo;
}
